import React, { createContext, useCallback, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Growl } from '../components/uxCore/Growl';
import { errorGrowl, successGrowl } from '../utils/growl';
import { GrowlContextType, GrowlMessageOptions } from '../types/Growl';
import { useDispatch } from '../hooks/useDispatch';
import { setThunkExtras } from '../redux/thunks/setThunkExtras';

type Props = {
  children: React.ReactNode;
};

export const GrowlContext = createContext<GrowlContextType>({
  showError: () => {},
  showSuccess: () => {}
});

export const GrowlProvider = ({ children }: Props) => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const parseMessage = useCallback(
    (options: GrowlMessageOptions) => {
      const { contentIntlKey, titleIntlKey, icon, fade, fadeTime } = options;
      return {
        icon,
        fade,
        fadeTime,
        ...(contentIntlKey ? { title: intl.formatMessage({ id: contentIntlKey }) } : {}),
        ...(titleIntlKey ? { content: intl.formatMessage({ id: titleIntlKey }) } : {})
      };
    },
    [intl]
  );

  const contextValue = useMemo(
    () => ({
      showError: (messageKey?: string, fade?: boolean) => {
        const growlOptions = errorGrowl(messageKey, fade);
        const growlMessage = parseMessage(growlOptions);
        Growl.addGrowlMessage(growlMessage);
      },
      showSuccess: (messageKey?: string, fade?: boolean) => {
        const growlOptions = successGrowl(messageKey, fade);
        const growlMessage = parseMessage(growlOptions);
        Growl.addGrowlMessage(growlMessage);
      }
    }),
    [parseMessage]
  );

  useEffect(() => {
    dispatch(setThunkExtras({ clientGrowl: contextValue }));
  }, [dispatch, contextValue]);

  return (
    <GrowlContext.Provider value={contextValue}>
      <Growl />
      {children}
    </GrowlContext.Provider>
  );
};
